import React, { useEffect, useState } from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import Grid from '@mui/material/Grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';
import Button from '../shared/Button';

import { userRoles } from '../../constants';

const rolesOptions =  Object.keys(userRoles).map((key) => ({
  label: userRoles[key].label,
  value: userRoles[key].name,
}));

const statusOptions = [{ label: 'Active', value: 'active' }, { label: 'Disabled', value: 'disabled' }];

const datePickerTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '8px',
    background: 'var(--color-surface)',
    '& fieldset': {
      borderColor: 'var(--color-border)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--color-border)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--color-main)',
      borderWidth: 1,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: 14,
    color: 'var(--color-text-primary)',
    padding: '8px 8px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--color-text-muted)',
    opacity: 1,
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--color-text-muted)',
  },
};

const FilterUsersModal = (props) => {
  const {
    filter,
    open,
    onSubmit,
    onClose,
    distributors,
    countries,
    companies,
  } = props;

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedDistributors, setSelectedDistributors] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [lastLoginFrom, setLastLoginFrom] = useState(null);
  const [lastLoginTo, setLastLoginTo] = useState(null);

  useEffect(() => {
    if (filter) {
      setSelectedCountries(filter.countries || []);
      setSelectedDistributors(filter.distributors?.length ? filter.distributors.map((distributorId) => distributors.find((d) => d.id === distributorId)) : []);
      setSelectedCompanies(filter.companies?.length ? filter.companies.map((companyId) => companies.find((c) => c.id === companyId)) : []);
      setSelectedRoles(filter.roles?.length ? filter.roles.map((name) => rolesOptions.find((r) => r.value === name)) : []);
      setSelectedStatuses(filter.statuses || []);
      setLastLoginFrom(filter.lastSignInTime?.from ? moment(filter.lastSignInTime.from) : null);
      setLastLoginTo(filter.lastSignInTime?.to ? moment(filter.lastSignInTime.to) : null);
    }
  }, [open, filter, distributors, companies]);

  const onClear = () => {
    setSelectedCountries([]);
    setSelectedDistributors([]);
    setSelectedCompanies([]);
    setSelectedRoles([]);
    setSelectedStatuses([]);
    setLastLoginFrom(null);
    setLastLoginTo(null);
  };

  const handleSubmit = () => {
    onSubmit({
      countries: selectedCountries,
      distributors: selectedDistributors?.map((d) => d.id),
      companies: selectedCompanies?.map((c) => c.id),
      roles: selectedRoles?.map((r) => r.value),
      statuses: selectedStatuses,
      lastSignInTime: lastLoginFrom || lastLoginTo ? { from: lastLoginFrom, to: lastLoginTo } : null,
    });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Filter Users"
      subtitle="Apply filters to find specific users"
      submitText="Submit"
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' }, maxWidth: '560px' }}>
        <Box>
          <Autocomplete
            value={selectedCompanies}
            onChange={setSelectedCompanies}
            options={companies}
            getOptionLabel={(option) => option.name}
            label="Company"
            multiple
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedCountries}
            onChange={setSelectedCountries}
            options={countries}
            label="Country"
            multiple
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedDistributors}
            onChange={setSelectedDistributors}
            options={distributors}
            label="Distributor"
            multiple
            getOptionLabel={(option) => option.name}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedRoles}
            onChange={setSelectedRoles}
            options={rolesOptions}
            label="Role"
            multiple
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
          />
        </Box>

        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            options={statusOptions}
            label="Status"
            multiple
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box sx={(theme) => ({ fontSize: 14, color: theme.palette.text.secondary, mb: 1 })}>
            Last Login
          </Box>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ fontSize: 14, mb: '2px' }}>From</Box>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={lastLoginFrom}
                  onChange={(date) => setLastLoginFrom(date)}
                  maxDate={lastLoginTo || undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      placeholder: 'Select date',
                      sx: datePickerTextFieldSx,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ fontSize: 14, mb: '2px' }}>To</Box>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={lastLoginTo}
                  onChange={(date) => setLastLoginTo(date)}
                  minDate={lastLoginFrom || undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      placeholder: 'Select date',
                      sx: datePickerTextFieldSx,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>

        <Button
          sx={{ mt: 2 }}
          text="Clear Filters"
          icon={<FilterAltOffOutlinedIcon />}
          onClick={onClear}
          type='secondary'
        />
      </Box>
    </Drawer>
  );
};

export default FilterUsersModal;
